import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { Input, Switch } from "@xola/ui-kit";
import { useClickAway, useMemoizedFn } from "ahooks";
import clsx from "clsx";
import { FormField, TextareaField } from "common";
import { debounce, isNil } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Coordinates, GoogleLocationResponse, MeetingLocation, NullableCoordinates } from "types";
import { Loader } from "@/components/Loader";
import { config } from "@/config";
import { buildMeetingLocationFromPlace } from "@/models/Experience";

const containerStyle = {
    width: "560px",
    height: "240px",
    borderRadius: "7px",
};

const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = { lat: 0, lng: 0 };

export interface MeetingLocationFieldProps {
    isMapEnabled: boolean;
    meetingLocation?: MeetingLocation;
    pickupAddress?: string;
    geo?: NullableCoordinates;
    onSelectPlace: (meetingLocation: MeetingLocation) => void;
    onSelectAddress: (address: string) => void;
    onClear: () => void;
    onAddressChange: (address: string) => void;
    onPinDrop: (geo: Coordinates) => void;
    onTurnOff: () => void;
    hasError?: boolean;
}

export const MeetingLocationField = React.memo(
    ({
        isMapEnabled,
        meetingLocation,
        pickupAddress,
        geo,
        onSelectPlace,
        onSelectAddress,
        onClear,
        onAddressChange,
        onPinDrop,
        onTurnOff,
        hasError = false,
    }: MeetingLocationFieldProps) => {
        const formattedAddress = meetingLocation?.formattedAddress;
        const [isCustomized, setIsCustomized] = useState(() => !formattedAddress);

        useEffect(() => {
            setIsCustomized(!formattedAddress);
        }, [formattedAddress]);

        const activeGeo = meetingLocation?.geo ?? geo;
        const activeLat = activeGeo?.lat;
        const activeLng = activeGeo?.lng;
        const hasValidGeoCoordinates = !isNil(activeLat) && !isNil(activeLng);
        const markerLocation = useMemo(
            () => (hasValidGeoCoordinates ? { lat: activeLat as number, lng: activeLng as number } : undefined),
            [hasValidGeoCoordinates, activeLat, activeLng],
        );

        const handleSelectPlace = (place: GoogleLocationResponse) => {
            const selectedLocation = buildMeetingLocationFromPlace(place);

            if (!isMapEnabled) {
                onSelectAddress(selectedLocation.formattedAddress ?? "");
                return;
            }

            setIsCustomized(false);
            onSelectPlace(selectedLocation);
        };

        const handleToggle = (value: boolean) => {
            setIsCustomized(value);

            if (!value) {
                onTurnOff();
            }
        };

        const addressValue = isMapEnabled ? meetingLocation?.formattedAddress ?? pickupAddress ?? "" : pickupAddress;

        return (
            <div className="space-y-3">
                <FormField name="pickupAddress" label="Address" className="w-full">
                    <PlaceSearchInput
                        name="pickupAddress"
                        value={addressValue ?? ""}
                        onSelect={handleSelectPlace}
                        onClear={onClear}
                        onChange={!isMapEnabled || isCustomized ? onAddressChange : undefined}
                    />
                </FormField>

                {isMapEnabled && (
                    <>
                        <div className="flex items-center space-x-2">
                            <Switch
                                size="medium"
                                isChecked={isCustomized}
                                data-testid="customize-pin-location-switch"
                                onChange={handleToggle}
                            />
                            <span>Customize pin location</span>
                        </div>

                        {(hasValidGeoCoordinates || isCustomized) && (
                            <MapComponent
                                center={markerLocation ?? DEFAULT_MAP_CENTER}
                                zoom={hasValidGeoCoordinates ? 14 : 1}
                                markerLocation={markerLocation}
                                hasError={hasError}
                                isCustomized={isCustomized}
                                onPinDrop={onPinDrop}
                            />
                        )}
                    </>
                )}

                <FormField name="meetingLocationInstructions" label="Additional Instructions">
                    <TextareaField rows={6} data-testid="meeting-location-instructions-textarea" />
                </FormField>
            </div>
        );
    },
);

interface PlaceSearchInputProps {
    name: string;
    value: string;
    onSelect: (place: GoogleLocationResponse) => void;
    onClear: () => void;
    onChange?: (text: string) => void;
}

// A minimal, fully-controlled Google Places search input. `@xola/ui-kit`'s GooglePlacesAutocomplete
// can't be used here: it always displays the selected place's short `name` (never its formatted
// address), and it re-runs an auto-search-and-select-first-result whenever it mounts with a non-empty
// initialValue, which fights any attempt to keep it in sync with an externally controlled value.
const PlaceSearchInput = ({ name, value, onSelect, onClear, onChange }: PlaceSearchInputProps) => {
    const [query, setQuery] = useState(value);
    const [results, setResults] = useState<GoogleLocationResponse[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearchError, setHasSearchError] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const latestRequestId = useRef(0);

    useEffect(() => {
        setQuery(value);
    }, [value]);

    useClickAway(() => setIsOpen(false), containerRef);

    const search = useMemo(
        () =>
            debounce(async (input: string) => {
                if (!input.trim()) {
                    setResults([]);
                    return;
                }

                const requestId = ++latestRequestId.current;
                setIsLoading(true);
                setHasSearchError(false);
                try {
                    const response = await fetch(
                        `${config.google.place_url}/searchGooglePlaces?input=${encodeURIComponent(input)}`,
                    );
                    if (!response.ok) {
                        throw new Error(`Search failed with status ${response.status}`);
                    }

                    const data = await response.json();
                    if (requestId !== latestRequestId.current) {
                        return;
                    }

                    setResults(Array.isArray(data) ? data : data.predictions ?? []);
                } catch {
                    if (requestId === latestRequestId.current) {
                        setResults([]);
                        setHasSearchError(true);
                    }
                } finally {
                    if (requestId === latestRequestId.current) {
                        setIsLoading(false);
                    }
                }
            }, 300),
        [],
    );

    useEffect(() => () => search.cancel(), [search]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setQuery(inputValue);
        setIsOpen(true);

        if (!inputValue.trim()) {
            setIsLoading(false);
            setHasSearchError(false);
            onClear();
        } else {
            setIsLoading(true);
            onChange?.(inputValue);
        }

        search(inputValue);
    };

    const handleSelect = (place: GoogleLocationResponse) => {
        setQuery(buildMeetingLocationFromPlace(place).formattedAddress ?? "");
        setResults([]);
        setIsOpen(false);
        onSelect(place);
    };

    return (
        <div ref={containerRef} className="relative">
            <Input
                type="text"
                name={name}
                placeholder="Search place..."
                value={query}
                onChange={handleChange}
                onFocus={() => setIsOpen(true)}
            />

            {isOpen && query.trim() && (
                <div className="absolute z-10 max-h-65 w-full overflow-y-auto rounded-md border border-gray bg-white shadow-lg">
                    {isLoading ? (
                        <div className="px-3 py-2 text-gray-dark">Loading...</div>
                    ) : hasSearchError ? (
                        <div className="px-3 py-2 text-danger">Search failed</div>
                    ) : results.length === 0 ? (
                        <div className="px-3 py-2 text-gray-dark">No results</div>
                    ) : (
                        results.map((place, index) => (
                            <div
                                key={place.place_id ?? `${place.name}-${place.formatted_address}-${index}`}
                                className="flex cursor-pointer flex-col gap-1 border-b border-gray bg-white px-3 py-2 hover:bg-gray-light hover:text-blue-light"
                                onClick={() => handleSelect(place)}
                            >
                                <p className="text-md">
                                    <var data-var="placeName">{place.name}</var>
                                </p>
                                <p className="text-sm text-gray-dark">
                                    <var data-var="placeAddress">{place.formatted_address}</var>
                                </p>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

interface MapComponentProps {
    center: google.maps.LatLngLiteral;
    zoom: number;
    markerLocation?: google.maps.LatLngLiteral;
    hasError: boolean;
    isCustomized: boolean;
    onPinDrop: (geo: google.maps.LatLngLiteral) => void;
}

const MapComponent = React.memo(
    ({ center, zoom, markerLocation, hasError, isCustomized, onPinDrop }: MapComponentProps) => {
        const { isLoaded } = useJsApiLoader({
            id: "google-map-script",
            googleMapsApiKey: config.google.apiKey,
        });

        // `@react-google-maps/api` binds its native click listener once and doesn't reliably rebind it
        // on re-render, so a plain closure here would keep using whatever `isCustomized`/`onPinDrop` were
        // current at that first binding. `useMemoizedFn` gives a stable reference whose body still always
        // reads the latest values.
        const handleMapClick = useMemoizedFn((...events: google.maps.MapMouseEvent[]) => {
            if (!isCustomized) {
                return;
            }

            const latlng = events[0]?.latLng;
            if (!isNil(latlng?.lat()) && !isNil(latlng?.lng())) {
                onPinDrop({ lat: latlng!.lat(), lng: latlng!.lng() });
            }
        });

        if (!isLoaded) {
            return <Loader size="tiny" className="p-2" text="Loading Map" />;
        }

        return (
            <div className={clsx(hasError && "rounded-lg border-2 border-danger")}>
                <GoogleMap
                    mapContainerClassName="no-translate"
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={zoom}
                    options={{ mapTypeControl: false, disableDefaultUI: true }}
                    onClick={handleMapClick}
                >
                    {markerLocation && <Marker position={markerLocation} />}
                </GoogleMap>
            </div>
        );
    },
);
